/**
 * metricCatalog — every counter the dashboard summary can show, in one list.
 *
 * "At a glance" used to hold four hard-coded tiles and `MetricsGrid` ten more,
 * with six of them defined twice in two files that had already drifted (the
 * same counter carried a different hint in each). One catalogue now, and the
 * two cards differ only in WHICH keys they render — which is exactly what makes
 * the glance card user-selectable: a choice is a list of keys, not a rewrite.
 *
 * A metric is a pure description: how to read it out of the overview, what to
 * call it, which licence section it belongs to, and where clicking it goes. No
 * component, no hook — so the picker can list the catalogue without rendering
 * anything, and a test can assert on it without a store.
 */

import React from 'react';
import {
  PiBriefcaseLight,
  PiCheckCircleLight,
  PiCheckSquareOffsetLight,
  PiFileLight,
  PiFileMagnifyingGlassLight,
  PiLinkSimpleLight,
  PiProjectorScreenChartLight,
  PiTableLight,
  PiUserListLight,
  PiWarningLight,
} from 'react-icons/pi';
import { DashboardOverview } from '../../../store/reducer/DashboardReducer';
import { FieldTone } from '../../shared/brand/Field';

export type MetricKey =
  | 'tasksOpen'
  | 'tasksOverdue'
  | 'tasksCompleted'
  | 'matters'
  | 'projects'
  | 'projectResources'
  | 'reviews'
  | 'clients'
  | 'tables'
  | 'files';

export interface MetricDefinition {
  key: MetricKey;
  /** Translation key — every consumer runs it through `t`. */
  label: string;
  /** Translation key for the qualifier under the label. */
  hint: string;
  icon: React.ReactNode;
  /** Licence section that must be open for this tile to exist. */
  section: string;
  route: string;
  value: (overview: DashboardOverview) => number;
  /**
   * Colour, and ONLY where colour is information: overdue work in `danger`,
   * completed work in `success`. Everything else is ink on glass.
   */
  tone?: (overview: DashboardOverview) => FieldTone | undefined;
  /**
   * A second line the tile can show instead of the static hint — the overdue
   * count under open tasks, the linked-resource count under projects.
   */
  dynamicHint?: (overview: DashboardOverview) => { key: string; count: number } | null;
}

export const METRIC_CATALOG: MetricDefinition[] = [
  {
    key: 'tasksOpen',
    label: 'Open Tasks',
    hint: 'All on schedule',
    icon: <PiCheckSquareOffsetLight />,
    section: 'tasks',
    route: '/tasks',
    value: (o) => o.tasks_open ?? 0,
    tone: (o) => ((o.tasks_overdue ?? 0) > 0 ? 'danger' : undefined),
    dynamicHint: (o) =>
      (o.tasks_overdue ?? 0) > 0 ? { key: '{{count}} overdue', count: o.tasks_overdue } : null,
  },
  {
    key: 'matters',
    label: 'Active Matters',
    hint: 'Open in your organisation',
    icon: <PiBriefcaseLight />,
    section: 'matters',
    route: '/matters',
    value: (o) => o.matters ?? 0,
  },
  {
    key: 'projects',
    label: 'Active Projects',
    hint: 'In progress',
    icon: <PiProjectorScreenChartLight />,
    section: 'projects',
    route: '/projects',
    value: (o) => o.projects_active ?? 0,
    dynamicHint: (o) => ({
      key: '{{count}} linked resources',
      count: o.project_resources_total ?? 0,
    }),
  },
  {
    key: 'reviews',
    label: 'Active Reviews',
    hint: 'Running right now',
    icon: <PiFileMagnifyingGlassLight />,
    section: 'reviews',
    route: '/reviews',
    value: (o) => o.reviews_active ?? 0,
  },
  {
    key: 'tasksOverdue',
    label: 'Overdue Tasks',
    hint: 'Past their due date',
    icon: <PiWarningLight />,
    section: 'tasks',
    route: '/tasks',
    value: (o) => o.tasks_overdue ?? 0,
    tone: (o) => ((o.tasks_overdue ?? 0) > 0 ? 'danger' : undefined),
  },
  {
    key: 'tasksCompleted',
    label: 'Tasks Completed',
    hint: 'Last 30 days',
    icon: <PiCheckCircleLight />,
    section: 'tasks',
    route: '/tasks',
    value: (o) => o.tasks_completed_30d ?? 0,
    tone: (o) => ((o.tasks_completed_30d ?? 0) > 0 ? 'success' : undefined),
  },
  {
    key: 'projectResources',
    label: 'Project Resources',
    hint: 'Linked to a project',
    icon: <PiLinkSimpleLight />,
    section: 'projects',
    route: '/projects',
    value: (o) => o.project_resources_total ?? 0,
  },
  {
    key: 'clients',
    label: 'Clients',
    hint: 'In your organisation',
    icon: <PiUserListLight />,
    section: 'contacts',
    route: '/contacts',
    value: (o) => o.clients ?? 0,
  },
  {
    key: 'tables',
    label: 'Tables',
    hint: 'Due diligence tables',
    icon: <PiTableLight />,
    section: 'tables',
    route: '/tables',
    value: (o) => o.tables ?? 0,
  },
  {
    key: 'files',
    label: 'Files',
    hint: 'In the vault',
    icon: <PiFileLight />,
    section: 'files',
    route: '/files',
    value: (o) => o.files ?? 0,
  },
];

export const METRIC_BY_KEY: Record<string, MetricDefinition> = METRIC_CATALOG.reduce(
  (acc, metric) => {
    acc[metric.key] = metric;
    return acc;
  },
  {} as Record<string, MetricDefinition>
);

/** What "At a glance" shows before anyone has picked — the four it shipped with. */
export const DEFAULT_GLANCE_KEYS: MetricKey[] = [
  'tasksOpen',
  'matters',
  'projects',
  'reviews',
];

/** Every key `MetricsGrid` renders — the full board. */
export const ALL_METRIC_KEYS: MetricKey[] = METRIC_CATALOG.map((metric) => metric.key);

/**
 * A stored selection, repaired.
 *
 * Storage is untrusted input here as everywhere: a key retired since it was
 * written is dropped, junk falls back to the default, and an empty result is
 * treated as "never chose" rather than as "chose nothing" — an At a glance card
 * with no tiles is indistinguishable from a broken one.
 */
export const validateMetricKeys = (raw: unknown): MetricKey[] => {
  if (!Array.isArray(raw)) return [...DEFAULT_GLANCE_KEYS];
  const seen = new Set<string>();
  const out: MetricKey[] = [];
  raw.forEach((value) => {
    if (typeof value !== 'string' || seen.has(value)) return;
    if (!METRIC_BY_KEY[value]) return;
    seen.add(value);
    out.push(value as MetricKey);
  });
  return out.length > 0 ? out : [...DEFAULT_GLANCE_KEYS];
};

export default METRIC_CATALOG;
